import { ApolloClient } from "@apollo/client";

import {
  RedeemEventActivityScanDocument,
  RedeemEventActivityScanMutation,
  RedeemEventActivityScanMutationVariables,
} from "src/views/volunteer/event-redemption/graphql/redeemEventActivityScan.generated";

import { RegisterBadgeCodeToEventAttendeeMutation } from "../graphql/registerBadgeCodeToEventAttendee.generated";

import {
  FindRegistrationActivityDocument,
  FindRegistrationActivityQuery,
  FindRegistrationActivityQueryVariables,
} from "./graphql/findRegistrationActivity.generated";

type RegisteredAttendee = Pick<
  RegisterBadgeCodeToEventAttendeeMutation["registerBadgeCodeToEventAttendee"],
  "id" | "event_id" | "badge_code"
>;

// Registration has already committed. A missing activity or failed scan must
// never retry badge allocation, interrupt programming, or undo that check-in.
export async function scanRegistrationActivity(
  client: ApolloClient<object>,
  attendee: RegisteredAttendee
): Promise<void> {
  let reason = "registered_badge_missing";
  try {
    console.info("registration_activity_scan_started", {
      eventId: attendee.event_id,
      eventAttendeeId: attendee.id,
    });
    if (!attendee.badge_code) throw new Error();

    reason = "activity_lookup_failed";
    const { data, errors } = await client.query<
      FindRegistrationActivityQuery,
      FindRegistrationActivityQueryVariables
    >({
      query: FindRegistrationActivityDocument,
      variables: { eventId: attendee.event_id },
      fetchPolicy: "no-cache",
      errorPolicy: "none",
    });
    if (errors?.length || !data) throw new Error();

    reason = "registration_activity_not_unique";
    if (data.findManyEventActivity.length !== 1) throw new Error();

    reason = "activity_scan_failed";
    const result = await client.mutate<
      RedeemEventActivityScanMutation,
      RedeemEventActivityScanMutationVariables
    >({
      mutation: RedeemEventActivityScanDocument,
      variables: {
        event_activity_id: data.findManyEventActivity[0].id,
        scan_input: attendee.badge_code,
      },
      errorPolicy: "none",
    });
    if (result.errors?.length || !result.data?.redeemEventActivityScan)
      throw new Error();

    const scan = result.data.redeemEventActivityScan;
    console.info("registration_activity_scan_succeeded", {
      eventId: attendee.event_id,
      eventAttendeeId: attendee.id,
      eventActivityId: data.findManyEventActivity[0].id,
      badgeScanId: scan.id,
      isDuplicate: scan.is_duplicate === true,
    });
  } catch {
    console.warn("registration_activity_scan_failed", {
      eventId: attendee.event_id,
      eventAttendeeId: attendee.id,
      reason,
    });
  }
}
